import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PRIMARY = '#2b6cb0';

function adminHeaders(json = false) {
  const h = { Authorization: `Bearer ${localStorage.getItem('hrv_admin_token')}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// ── Status badges ─────────────────────────────────────────────────────────────

function StatusBadge({ user }) {
  if (user.is_deleted)   return <Pill bg="#fff5f5" color="#c53030" border="#fed7d7">Deleted</Pill>;
  if (user.is_suspended) return <Pill bg="#fffbeb" color="#744210" border="#fbd38d">Suspended</Pill>;
  return <Pill bg="#f0fff4" color="#276749" border="#9ae6b4">Active</Pill>;
}

function PlanBadge({ tier, status, expires_at }) {
  if (status === 'expired') return <Pill bg="#fff5f5" color="#c53030" border="#fed7d7">Pro (expired)</Pill>;
  if (tier === 'pro') return <Pill bg="#ebf4ff" color={PRIMARY} border="#bee3f8">Pro · {fmtDate(expires_at)}</Pill>;
  return <Pill bg="#f7fafc" color="#718096" border="#e2e8f0">Free</Pill>;
}

function Pill({ bg, color, border, children }) {
  return (
    <span style={{ display:'inline-block', padding:'2px 9px', borderRadius:'20px', fontSize:'0.72rem', fontWeight:'700', background:bg, color, border:`1px solid ${border}` }}>
      {children}
    </span>
  );
}

// ── Field row ─────────────────────────────────────────────────────────────────

function Field({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', background:'#f7fafc', borderRadius:'7px', marginBottom:'6px' }}>
      <span style={{ color:'#a0aec0', fontSize:'0.72rem', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.05em', alignSelf:'center' }}>{label}</span>
      <span style={{ color:'#2d3748', fontSize:'0.82rem', fontWeight:'600', fontFamily: mono ? 'monospace' : undefined, textAlign:'right', maxWidth:'200px', wordBreak:'break-all' }}>{value}</span>
    </div>
  );
}

// ── Patient card ──────────────────────────────────────────────────────────────

function PatientCard({ p, userId, onToggle }) {
  const [toggling, setToggling] = useState(false);
  const isActive = p.is_active !== false;

  const handleToggle = async () => {
    setToggling(true);
    try {
      await fetch(`${API_URL}/admin/users/${userId}/patients/${p.patient_id}/toggle`, { method:'POST', headers:adminHeaders() });
      onToggle();
    } finally { setToggling(false); }
  };

  return (
    <div style={{ border:`1px solid ${isActive ? '#e2e8f0' : '#fed7d7'}`, borderRadius:'10px', padding:'14px', marginBottom:'10px', background: isActive ? 'white' : '#fff8f8', opacity: isActive ? 1 : 0.75 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
        <div>
          <div style={{ fontWeight:'700', color:'#1a202c', fontSize:'0.9rem' }}>{p.name}</div>
          <div style={{ color:'#718096', fontSize:'0.75rem', marginTop:'2px' }}>
            {p.patient_id} · {p.relationship || 'unknown relation'}
          </div>
        </div>
        <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
          {!isActive && <Pill bg="#fff5f5" color="#c53030" border="#fed7d7">Inactive</Pill>}
          <button onClick={handleToggle} disabled={toggling} style={{
            padding:'3px 10px', fontSize:'0.72rem', fontWeight:'600', borderRadius:'6px', cursor:'pointer', border:'1px solid',
            background: isActive ? '#fffbeb' : '#f0fff4',
            color: isActive ? '#744210' : '#276749',
            borderColor: isActive ? '#fbd38d' : '#9ae6b4',
          }}>
            {toggling ? '…' : isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
        {p.date_of_birth && <Field label="DOB" value={fmtDate(p.date_of_birth)} />}
        {p.gender && <Field label="Gender" value={p.gender} />}
        {p.phone && <Field label="Phone" value={p.phone} />}
        {p.email && <Field label="Email" value={p.email} />}
        {p.blood_type && <Field label="Blood Type" value={p.blood_type} />}
      </div>

      {(p.emergency_contact_name || p.emergency_contact_phone) && (
        <div style={{ marginTop:'8px', padding:'8px 10px', background:'#fffbeb', borderRadius:'6px', border:'1px solid #fbd38d' }}>
          <div style={{ fontSize:'0.68rem', fontWeight:'800', color:'#92400e', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'4px' }}>Emergency Contact</div>
          <div style={{ fontSize:'0.8rem', color:'#744210', fontWeight:'600' }}>
            {p.emergency_contact_name || '—'}
            {p.emergency_contact_relation ? ` (${p.emergency_contact_relation})` : ''}
          </div>
          {p.emergency_contact_phone && (
            <div style={{ fontSize:'0.78rem', color:'#744210', marginTop:'2px' }}>{p.emergency_contact_phone}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Usage bar ─────────────────────────────────────────────────────────────────

function UsageBar({ label, used, limit, hardLimit }) {
  const pct = limit ? Math.min((used / limit) * 100, 100) : 0;
  const color = pct >= 100 ? '#c53030' : pct >= 80 ? '#d69e2e' : '#38a169';
  return (
    <div style={{ marginBottom:'8px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}>
        <span style={{ fontSize:'0.75rem', color:'#4a5568', fontWeight:'600' }}>{label}</span>
        <span style={{ fontSize:'0.75rem', color, fontWeight:'700' }}>{used}{limit ? ` / ${limit}` : ''}{hardLimit ? ` (hard cap: ${hardLimit})` : ''}</span>
      </div>
      {limit && (
        <div style={{ height:'5px', background:'#e2e8f0', borderRadius:'3px' }}>
          <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:'3px', transition:'width 0.3s' }} />
        </div>
      )}
    </div>
  );
}

// ── Plan editor ───────────────────────────────────────────────────────────────

function PlanEditor({ user, onSave }) {
  const [tier, setTier] = useState(user.plan_tier || 'free');
  const [expires, setExpires] = useState(user.plan_expires_at ? user.plan_expires_at.slice(0,10) : '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API_URL}/admin/users/${user.id}/plan`, {
        method:'PUT', headers:adminHeaders(true),
        body: JSON.stringify({ plan_tier: tier, plan_expires_at: tier === 'pro' ? expires : null }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setMsg('Saved');
      onSave();
      setTimeout(() => setMsg(''), 2000);
    } catch(e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ background:'#f7fafc', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
      <div style={{ fontSize:'0.72rem', fontWeight:'800', color:'#a0aec0', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'10px' }}>Subscription Plan</div>
      <div style={{ display:'flex', gap:'8px', marginBottom: tier === 'pro' ? '10px' : '0' }}>
        {['free','pro'].map(t => (
          <button key={t} onClick={() => setTier(t)} style={{
            padding:'6px 18px', borderRadius:'7px', border:`2px solid ${tier===t ? PRIMARY : '#e2e8f0'}`,
            background: tier===t ? PRIMARY : 'white', color: tier===t ? 'white' : '#4a5568',
            fontWeight:'700', fontSize:'0.82rem', cursor:'pointer', textTransform:'capitalize',
          }}>
            {t === 'pro' ? 'Pro' : 'Free'}
          </button>
        ))}
      </div>
      {tier === 'pro' && (
        <div style={{ marginTop:'10px' }}>
          <label style={{ display:'block', fontSize:'0.75rem', fontWeight:'600', color:'#4a5568', marginBottom:'4px' }}>Expires on</label>
          <input type="date" value={expires} onChange={e => setExpires(e.target.value)}
            style={{ padding:'7px 10px', border:'1px solid #e2e8f0', borderRadius:'7px', fontSize:'0.85rem', width:'100%', boxSizing:'border-box' }} />
        </div>
      )}
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'10px' }}>
        <button onClick={handleSave} disabled={saving} style={{
          padding:'7px 18px', background:PRIMARY, color:'white', border:'none', borderRadius:'7px', fontWeight:'700', fontSize:'0.82rem', cursor:'pointer'
        }}>
          {saving ? 'Saving…' : 'Update Plan'}
        </button>
        {msg && <span style={{ fontSize:'0.8rem', color: msg === 'Saved' ? '#276749' : '#c53030', fontWeight:'600' }}>{msg}</span>}
      </div>
    </div>
  );
}

// ── User Detail Panel ─────────────────────────────────────────────────────────

function UserDetailPanel({ userId, adminRole, onClose, onRefresh }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg]   = useState('');
  const [confirm, setConfirm] = useState(null); // 'delete' | 'restore' | null
  const [working, setWorking] = useState('');
  const [ghosting, setGhosting] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setErrMsg('');
    fetch(`${API_URL}/admin/users/${userId}`, { headers: adminHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.error) setErrMsg(d.error);
        else setDetail(d);
      })
      .catch(() => setErrMsg('Network error'))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const action = async (url, method = 'POST') => {
    setWorking(url);
    await fetch(`${API_URL}${url}`, { method, headers: adminHeaders() });
    setWorking('');
    setConfirm(null);
    onRefresh();
    load();
  };

  const handleGhost = async () => {
    setGhosting(true);
    try {
      const res = await fetch(`${API_URL}/admin/ghost/${userId}`, { method:'POST', headers: adminHeaders() });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed');

      // Save any existing regular session so exit can restore it
      const prevToken   = localStorage.getItem('token');
      const prevUser    = localStorage.getItem('user');
      const prevRefresh = localStorage.getItem('refresh_token');

      // ghost_mode: new code uses this for banner + identity
      localStorage.setItem('ghost_mode', JSON.stringify({
        active: true,
        token: d.token,
        user: d.user,
        as_user: d.user.email,
        expires_at: d.expires_at,
        prev_token: prevToken,
        prev_user: prevUser,
        prev_refresh: prevRefresh,
      }));

      // Also write to regular token/user so cached (old) JS can read them too
      localStorage.setItem('token', d.token);
      localStorage.setItem('user', JSON.stringify(d.user));
      localStorage.removeItem('refresh_token'); // ghost token can't be refreshed

      // Open in new tab — admin panel stays open here
      window.open('/dashboard', '_blank');
    } catch(e) {
      alert('Ghost login failed: ' + e.message);
    } finally { setGhosting(false); }
  };

  const u = detail?.user;
  const usage = detail?.usage || {};
  const isSuperAdmin = adminRole === 'super_admin';

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.3)', zIndex:199 }} />

      <div style={{
        position:'fixed', top:0, right:0, bottom:0, width:'460px',
        background:'white', boxShadow:'-4px 0 24px rgba(0,0,0,0.15)',
        zIndex:200, display:'flex', flexDirection:'column',
      }}>
        {/* Header */}
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center', background:PRIMARY }}>
          <h3 style={{ margin:0, fontSize:'1rem', fontWeight:'700', color:'white' }}>User Details</h3>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'6px', width:'28px', height:'28px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'white', fontSize:'1.1rem' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 22px' }}>
          {loading ? (
            <div style={{ textAlign:'center', paddingTop:'60px', color:'#718096' }}>Loading…</div>
          ) : errMsg ? (
            <div style={{ textAlign:'center', paddingTop:'60px', color:'#c53030' }}>
              <div style={{ fontSize:'1.2rem', marginBottom:'8px' }}>⚠</div>
              {errMsg}
            </div>
          ) : u ? (<>
            {/* Identity */}
            <div style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'18px' }}>
              <div style={{ width:'52px', height:'52px', borderRadius:'50%', background:'#bee3f8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.3rem', fontWeight:'800', color:PRIMARY, flexShrink:0 }}>
                {u.first_name?.[0]}{u.last_name?.[0]}
              </div>
              <div>
                <div style={{ fontWeight:'800', color:'#1a202c', fontSize:'1.05rem' }}>{u.first_name} {u.last_name}</div>
                <div style={{ color:'#718096', fontSize:'0.82rem', marginTop:'2px' }}>{u.email}</div>
                <div style={{ display:'flex', gap:'5px', marginTop:'5px', flexWrap:'wrap' }}>
                  <StatusBadge user={u} />
                  <PlanBadge tier={u.plan_tier} status={u.plan_status} expires_at={u.plan_expires_at} />
                  {u.is_google_user && <Pill bg="#f7fafc" color="#718096" border="#e2e8f0">Google</Pill>}
                </div>
              </div>
            </div>

            {/* User fields */}
            <div style={{ marginBottom:'16px' }}>
              <Field label="User ID" value={u.id} mono />
              <Field label="Phone" value={u.phone} />
              <Field label="Date of Birth" value={fmtDate(u.date_of_birth)} />
              <Field label="Gender" value={u.gender} />
              <Field label="Joined" value={fmtDate(u.created_at)} />
              <Field label="Last Login" value={fmtDateTime(u.last_login)} />
              {u.is_deleted && <Field label="Deleted On" value={fmtDate(u.deleted_at)} />}
            </div>

            {/* Plan */}
            {isSuperAdmin && <PlanEditor user={u} onSave={() => { onRefresh(); load(); }} />}

            {/* Usage */}
            <div style={{ background:'#f7fafc', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
              <div style={{ fontSize:'0.72rem', fontWeight:'800', color:'#a0aec0', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'10px' }}>Usage (Free Plan Limits)</div>
              <UsageBar label="Vitals"         used={usage.vital_count   ?? 0} limit={u.plan_tier === 'free' ? 10 : null} />
              <UsageBar label="Documents"      used={usage.doc_count     ?? 0} limit={u.plan_tier === 'free' ? 5  : null} />
              <UsageBar label="Lab Parameters" used={usage.lab_count     ?? 0} limit={u.plan_tier === 'free' ? 100 : null} hardLimit={u.plan_tier === 'free' ? 150 : null} />
              <UsageBar label="Patients"       used={usage.patient_count ?? 0} limit={null} />
            </div>

            {/* Patients */}
            <div style={{ marginBottom:'16px' }}>
              <div style={{ fontSize:'0.72rem', fontWeight:'800', color:'#a0aec0', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'10px' }}>
                Patients ({detail.patients?.length || 0})
              </div>
              {detail.patients?.length ? detail.patients.map(p => (
                <PatientCard key={p.patient_id} p={p} userId={u.id} onToggle={load} />
              )) : (
                <div style={{ color:'#a0aec0', fontSize:'0.875rem', textAlign:'center', padding:'20px' }}>No patients added</div>
              )}
            </div>

          </>) : null}
        </div>

        {/* Actions footer */}
        {u && isSuperAdmin && (
          <div style={{ padding:'16px 22px', borderTop:'1px solid #e2e8f0', display:'flex', flexDirection:'column', gap:'8px' }}>

            {/* Ghost login */}
            {!u.is_deleted && (
              <button onClick={handleGhost} disabled={ghosting} style={{
                padding:'10px', borderRadius:'8px', fontWeight:'700', fontSize:'0.875rem', cursor:'pointer',
                background:'#1a202c', color:'white', border:'none', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px',
              }}>
                {ghosting ? 'Opening session…' : '👁 Ghost Login — View as this User'}
              </button>
            )}

            {/* Suspend / Unsuspend */}
            {!u.is_deleted && (
              <button onClick={() => action(`/admin/users/${u.id}/suspend`)} disabled={!!working} style={{
                padding:'9px', borderRadius:'8px', fontWeight:'600', fontSize:'0.875rem', cursor:'pointer',
                background: u.is_suspended ? '#f0fff4' : '#fffbeb',
                color: u.is_suspended ? '#276749' : '#744210',
                border: `1px solid ${u.is_suspended ? '#9ae6b4' : '#fbd38d'}`,
              }}>
                {working.includes('suspend') ? '…' : u.is_suspended ? 'Unsuspend User' : 'Suspend User'}
              </button>
            )}

            {/* Soft Delete / Restore */}
            {!u.is_deleted ? (
              confirm === 'delete' ? (
                <div style={{ display:'flex', gap:'6px' }}>
                  <button onClick={() => action(`/admin/users/${u.id}`, 'DELETE')} style={{ flex:1, padding:'9px', borderRadius:'8px', background:'#c53030', color:'white', border:'none', fontWeight:'700', cursor:'pointer', fontSize:'0.875rem' }}>
                    Confirm Delete (data kept)
                  </button>
                  <button onClick={() => setConfirm(null)} style={{ padding:'9px 14px', borderRadius:'8px', background:'#f7fafc', border:'1px solid #e2e8f0', color:'#4a5568', cursor:'pointer', fontWeight:'600' }}>Cancel</button>
                </div>
              ) : (
                <button onClick={() => setConfirm('delete')} style={{
                  padding:'9px', borderRadius:'8px', fontWeight:'600', fontSize:'0.875rem', cursor:'pointer',
                  background:'#fff5f5', color:'#c53030', border:'1px solid #fed7d7',
                }}>
                  Delete Account (data preserved)
                </button>
              )
            ) : (
              <button onClick={() => action(`/admin/users/${u.id}/restore`)} style={{
                padding:'9px', borderRadius:'8px', fontWeight:'700', fontSize:'0.875rem', cursor:'pointer',
                background:'#f0fff4', color:'#276749', border:'1px solid #9ae6b4',
              }}>
                Restore Account
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminUsers() {
  const [users, setUsers]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all');
  const [loading, setLoading]   = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const adminInfo = JSON.parse(localStorage.getItem('hrv_admin_info') || '{}');
  const limit = 20;

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit, search, filter }).toString();
    fetch(`${API_URL}/admin/users?${params}`, { headers: adminHeaders() })
      .then(r => r.json())
      .then(d => { setUsers(d.users || []); setTotal(d.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, filter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const totalPages = Math.ceil(total / limit);

  const FILTERS = [
    { value:'all',       label:'All Active' },
    { value:'active_7d', label:'Active 7d' },
    { value:'active_30d',label:'Active 30d' },
    { value:'inactive',  label:'Inactive' },
    { value:'suspended', label:'Suspended' },
    { value:'deleted',   label:'Deleted' },
  ];

  return (
    <AdminLayout>
      {selectedId && (
        <UserDetailPanel
          userId={selectedId}
          adminRole={adminInfo.role}
          onClose={() => setSelectedId(null)}
          onRefresh={fetchUsers}
        />
      )}

      <div style={{ marginBottom:'22px', display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
        <div>
          <h1 style={{ fontSize:'1.5rem', fontWeight:'800', color:'#1a202c', margin:0 }}>User Management</h1>
          <p style={{ color:'#718096', margin:'4px 0 0', fontSize:'0.85rem' }}>{total} users in this view</p>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display:'flex', gap:'10px', marginBottom:'18px', flexWrap:'wrap', alignItems:'center' }}>
        <input
          value={search} placeholder="Search by name or email…"
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ flex:1, minWidth:'200px', padding:'9px 14px', border:`2px solid #e2e8f0`, borderRadius:'8px', fontSize:'0.875rem', outline:'none' }}
        />
        <div style={{ display:'flex', gap:'4px', background:'white', padding:'4px', border:'1px solid #e2e8f0', borderRadius:'8px' }}>
          {FILTERS.map(f => (
            <button key={f.value} onClick={() => { setFilter(f.value); setPage(1); }} style={{
              padding:'5px 12px', borderRadius:'6px', border:'none', fontSize:'0.78rem', fontWeight:'600', cursor:'pointer',
              background: filter === f.value ? PRIMARY : 'transparent',
              color: filter === f.value ? 'white' : '#718096',
            }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ background:'white', borderRadius:'12px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden', border:'1px solid #e2e8f0' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#f7fafc', borderBottom:'2px solid #e2e8f0' }}>
              {['Name / Email', 'Joined', 'Last Active', 'Patients', 'Docs', 'Plan', 'Status', ''].map(h => (
                <th key={h} style={{ padding:'11px 14px', textAlign:'left', fontSize:'0.7rem', fontWeight:'800', color:'#a0aec0', textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding:'50px', textAlign:'center', color:'#a0aec0' }}>Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={8} style={{ padding:'50px', textAlign:'center', color:'#a0aec0' }}>No users found</td></tr>
            ) : users.map((u, i) => (
              <tr key={u.id} style={{
                borderBottom:'1px solid #f0f4f8',
                background: u.is_deleted ? '#fff8f8' : i % 2 === 0 ? 'white' : '#fafafa',
                opacity: u.is_deleted ? 0.75 : 1,
              }}>
                <td style={{ padding:'11px 14px' }}>
                  <div style={{ fontWeight:'700', color:'#2d3748', fontSize:'0.875rem' }}>{u.first_name} {u.last_name}</div>
                  <div style={{ color:'#a0aec0', fontSize:'0.75rem', marginTop:'1px' }}>{u.email}</div>
                </td>
                <td style={{ padding:'11px 14px', color:'#718096', fontSize:'0.78rem', whiteSpace:'nowrap' }}>{fmtDate(u.created_at)}</td>
                <td style={{ padding:'11px 14px', color:'#718096', fontSize:'0.78rem', whiteSpace:'nowrap' }}>{u.last_login ? fmtDate(u.last_login) : <span style={{ color:'#cbd5e0' }}>Never</span>}</td>
                <td style={{ padding:'11px 14px' }}>
                  <div style={{ fontWeight:'600', color:'#4a5568', fontSize:'0.875rem', marginBottom: u.patient_ids?.length ? '4px' : 0 }}>{u.patient_count}</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'3px' }}>
                    {(u.patient_ids || []).map(pid => (
                      <span key={pid} style={{ fontSize:'0.65rem', fontWeight:'700', color:'#2b6cb0', background:'#ebf8ff', border:'1px solid #bee3f8', borderRadius:'4px', padding:'1px 5px', letterSpacing:'0.02em' }}>{pid}</span>
                    ))}
                  </div>
                </td>
                <td style={{ padding:'11px 14px', textAlign:'center', color:'#4a5568', fontSize:'0.875rem', fontWeight:'600' }}>{u.document_count}</td>
                <td style={{ padding:'11px 14px' }}><PlanBadge tier={u.plan_tier} status={u.plan_status} expires_at={u.plan_expires_at} /></td>
                <td style={{ padding:'11px 14px' }}><StatusBadge user={u} /></td>
                <td style={{ padding:'11px 14px' }}>
                  <button onClick={() => setSelectedId(u.id)} style={{
                    padding:'5px 12px', background:'#edf2f7', color:'#4a5568',
                    border:'none', borderRadius:'6px', fontSize:'0.78rem', cursor:'pointer', fontWeight:'700',
                  }}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div style={{ padding:'14px', display:'flex', justifyContent:'center', gap:'8px', borderTop:'1px solid #e2e8f0', alignItems:'center' }}>
            <button onClick={() => setPage(p => Math.max(p-1,1))} disabled={page<=1}
              style={{ padding:'6px 14px', borderRadius:'6px', border:'1px solid #e2e8f0', background:'white', cursor:'pointer', color:'#4a5568', fontWeight:'600' }}>← Prev</button>
            <span style={{ padding:'6px 14px', color:'#718096', fontSize:'0.82rem' }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(p+1,totalPages))} disabled={page>=totalPages}
              style={{ padding:'6px 14px', borderRadius:'6px', border:'1px solid #e2e8f0', background:'white', cursor:'pointer', color:'#4a5568', fontWeight:'600' }}>Next →</button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
